import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { DynamicRoleAssignmentRequestDto } from "@/types/auth";

import { queryKeys } from "./queryKeys";

export const useDynamicRoleAssignmentsQuery = () => {
  const { getDynamicRoleAssignments } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.DYNAMIC_ROLE_ASSIGNMENTS, currentCompany.id],
    queryFn: () => getDynamicRoleAssignments(currentCompany.id),
    staleTime: 30000,
  });
};

/**
 * Active users each role reaches through its dynamic rules, counted live. Loaded apart from the
 * roles list so the list renders first; nested under the assignments key, so saving or deleting a
 * rule refetches it.
 */
export const useMatchedUsersByRoleQuery = () => {
  const { getMatchedUsersByRole } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.DYNAMIC_ROLE_ASSIGNMENTS, currentCompany.id, "matchedUsers"],
    queryFn: () => getMatchedUsersByRole(currentCompany.id),
    staleTime: 30000,
    retry: false,
  });
};

const useInvalidateAssignments = () => {
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [queryKeys.DYNAMIC_ROLE_ASSIGNMENTS, currentCompany.id],
    });
};

export const useCreateDynamicRoleAssignmentMutation = () => {
  const { createDynamicRoleAssignment } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();
  const invalidate = useInvalidateAssignments();

  return useMutation({
    mutationFn: (dto: DynamicRoleAssignmentRequestDto) =>
      createDynamicRoleAssignment(currentCompany.id, dto),
    onSuccess: invalidate,
  });
};

export const useUpdateDynamicRoleAssignmentMutation = () => {
  const { updateDynamicRoleAssignment } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();
  const invalidate = useInvalidateAssignments();

  return useMutation({
    mutationFn: (variables: { id: number; dto: DynamicRoleAssignmentRequestDto }) =>
      updateDynamicRoleAssignment(currentCompany.id, variables.id, variables.dto),
    onSuccess: invalidate,
  });
};

export const useDeleteDynamicRoleAssignmentMutation = () => {
  const { deleteDynamicRoleAssignment } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();
  const invalidate = useInvalidateAssignments();

  return useMutation({
    mutationFn: (id: number) => deleteDynamicRoleAssignment(currentCompany.id, id),
    onSuccess: invalidate,
  });
};

/**
 * Who a candidate still being edited would reach. The serialised body is debounced into the query
 * key, so keystrokes collapse into one request; `undefined` while the form is incomplete for its mode.
 */
export const useDynamicRoleAssignmentPreviewQuery = (
  request: DynamicRoleAssignmentRequestDto | undefined,
) => {
  const { postDynamicRoleAssignmentPreview } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();
  const [body] = useDebounce(request ? JSON.stringify(request) : "", 500);

  return useQuery({
    queryKey: [queryKeys.DYNAMIC_ROLE_ASSIGNMENT_PREVIEW, currentCompany.id, body],
    queryFn: () => postDynamicRoleAssignmentPreview(currentCompany.id, JSON.parse(body)),
    enabled: body.length > 0,
    staleTime: Infinity,
    retry: false,
  });
};

/** Who a saved rule reaches, for the read-only panel. Names come back only for callers who may read every user. */
export const useStoredDynamicRoleAssignmentPreviewQuery = (id: number) => {
  const { getDynamicRoleAssignmentPreview } = useRestApi().dynamicRoles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.DYNAMIC_ROLE_ASSIGNMENT_STORED_PREVIEW, currentCompany.id, id],
    queryFn: () => getDynamicRoleAssignmentPreview(currentCompany.id, id),
    staleTime: 30000,
    retry: false,
  });
};
